import { WORLDS, BRANDS } from "../data/content";
import LogoMark from "./LogoMark";
import Flower from "./Flower";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";

const Connector = ({ label }) => (
  <div className="flex flex-col items-center my-1" aria-hidden="true">
    <div className="h-9 border-l-2 border-dashed border-[#7C9A4E]/60" />
    <Flower className="w-9 h-9 my-1 animate-floaty" petals="#E85D9E" />
    <div className="h-9 border-l-2 border-dashed border-[#7C9A4E]/60" />
    {label && (
      <>
        <p className="font-hand text-2xl text-[#5A7033] my-2 -rotate-1">{label}</p>
        <div className="h-9 border-l-2 border-dashed border-[#7C9A4E]/60" />
      </>
    )}
  </div>
);

export default function BrandMap() {
  return (
    <section id="map" data-testid="brand-map-section" className="relative bg-[#F0F5E6] text-[#3F511F] py-24 md:py-32 overflow-hidden">
      <div className="absolute top-16 left-[6%] animate-floaty" style={{ "--r": "-8deg" }} aria-hidden="true">
        <Flower className="w-14 h-14 opacity-70" petals="#E3B341" center="#E85D9E" />
      </div>
      <div className="absolute bottom-24 right-[5%] animate-floaty" style={{ "--r": "6deg", animationDelay: "1.1s" }} aria-hidden="true">
        <Flower className="w-10 h-10 opacity-70" petals="#4E9B94" center="#FBF3E4" />
      </div>

      <div className="relative max-w-5xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow="Visual brand map — 003 · follow the petals"
          title={<>How it all <span className="italic text-en-green">grows.</span></>}
          note="one seed. many blooms. zero confusion about where it started."
          noteColor="text-en-orange"
        />

        <div className="flex flex-col items-center">
          <Reveal>
            <div data-testid="map-node-hub" className="relative flex items-center gap-4 rounded-full bg-white border-2 border-[#7C9A4E]/40 shadow-lg pl-4 pr-8 py-4 -rotate-1 hover:rotate-0 transition-transform">
              <div className="absolute -top-4 -left-4" aria-hidden="true">
                <Flower className="w-9 h-9" />
              </div>
              <LogoMark className="w-11 h-11 text-[#3F511F]" />
              <div>
                <p className="font-display font-black text-xl md:text-2xl leading-none">ECLECTICALLY NELLE</p>
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#5A7033]/70 mt-1">the hub of it all</p>
              </div>
            </div>
          </Reveal>

          <Connector label="the creative worlds" />

          <div data-testid="map-node-worlds" className="flex flex-wrap justify-center gap-3 max-w-3xl">
            {WORLDS.map((w, i) => (
              <Reveal key={w.id} delay={i * 0.06} y={16}>
                <span
                  className={`inline-flex items-center gap-2 rounded-full bg-white border-2 shadow-sm pl-2 pr-4 py-2 font-mono text-[10px] md:text-[11px] tracking-[0.15em] uppercase hover:scale-105 transition-transform ${i % 2 ? "rotate-1" : "-rotate-1"}`}
                  style={{ borderColor: `${w.color}55` }}
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: w.color }} />
                  {w.name}
                </span>
              </Reveal>
            ))}
          </div>

          <Connector label="individual brands & projects" />

          <div data-testid="map-node-brands" className="flex justify-center w-full max-w-3xl">
            {BRANDS.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.1} className="w-full max-w-xs">
                <div className="relative rounded-3xl bg-white border border-[#7C9A4E]/25 shadow-sm overflow-visible h-full hover:shadow-lg transition-shadow -rotate-1">
                  <div className="absolute -top-3 -right-3" aria-hidden="true">
                    <Flower className="w-8 h-8" petals={b.accent} center="#FBF3E4" />
                  </div>
                  <div className="h-2 rounded-t-3xl" style={{ backgroundColor: b.accent }} />
                  <div className="p-5">
                    <p className="font-display font-bold text-lg leading-tight">{b.name}</p>
                    <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#5A7033]/60 mt-2">{b.tag}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
